import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Star, ShoppingCart, Check, Info, Clock } from 'lucide-react';
import { useApp } from '../context/AppContext';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';

const ServiceCategory = () => {
  const { categoryId } = useParams();
  const nav = useNavigate();
  const { addToCart, cart, services } = useApp();
  const data = services[categoryId] || { title: 'Services', items: [] };
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      {/* Mobile sticky header */}
      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-neutral-100">
        <div className="px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-lg font-extrabold text-neutral-900">{data.title}</h1>
          </div>
          <button onClick={() => nav('/cart')} className="relative w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center">
            <ShoppingCart className="w-4 h-4" />
            {cartCount > 0 && <span className="absolute -top-1 -right-1 bg-[#EA580C] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{cartCount}</span>}
          </button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-5 md:px-6 pt-4 md:pt-6">
        {/* Desktop breadcrumb */}
        <div className="hidden md:flex items-center gap-2 text-sm text-neutral-500 mb-3">
          <button onClick={() => nav('/home')} className="hover:text-neutral-900">Home</button>
          <span>/</span>
          <span className="text-neutral-900 font-medium">{data.title}</span>
        </div>
        <div className="hidden md:flex items-end justify-between mb-5">
          <div>
            <h1 className="text-2xl font-extrabold text-neutral-900">{data.title}</h1>
            <p className="text-sm text-neutral-600 mt-0.5">{data.items.length} service{data.items.length > 1 ? 's' : ''} available • Verified professionals</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {/* Left: Service list (2 cols on desktop) */}
          <div className="md:col-span-2 space-y-3">
            {data.items.map((item) => {
              const inCart = cart.find((c) => c.id === item.id);
              return (
                <div key={item.id} onClick={() => nav(`/services/${categoryId}/${item.id}`)} className="bg-white border border-neutral-200 rounded-xl p-3 md:p-4 flex gap-3 md:gap-4 hover:shadow-md hover:border-[#2E7D32] transition-all cursor-pointer">
                  <div className="relative w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden bg-neutral-100 flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    <div className="absolute top-1 left-1 bg-white/95 text-[9px] font-bold text-[#2E7D32] px-1.5 py-0.5 rounded">Save {item.save}%</div>
                  </div>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <h3 className="font-bold text-neutral-900 text-sm md:text-base leading-tight">{item.name}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-3.5 h-3.5 text-[#F5B301] fill-[#F5B301]" />
                      <span className="text-xs md:text-sm font-semibold">{item.rating}</span>
                      <span className="text-[11px] text-neutral-500">({item.reviews} reviews)</span>
                      {item.basis && (
                        <span className="ml-1 text-[10px] font-bold text-[#2E7D32] bg-[#E8F5E9] px-1.5 py-0.5 rounded-full">{item.basis}</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs md:text-sm text-neutral-600 leading-snug line-clamp-2">{item.desc}</p>
                    <button onClick={(e) => { e.stopPropagation(); nav(`/services/${categoryId}/${item.id}`); }} className="mt-1 self-start text-[11px] font-bold text-[#EA580C] hover:underline">View Details →</button>
                    <div className="mt-auto pt-2 flex items-end justify-between">
                      <div className="flex items-baseline gap-2">
                        {item.pricing_type === 'hourly' && Number(item.hourly_rate) > 0 ? (
                          <>
                            <span className="text-lg md:text-xl font-extrabold text-[#EA580C]" data-testid={`svc-price-${item.id}`}>₹{item.hourly_rate}</span>
                            <span className="text-xs text-neutral-500 font-semibold">/ hr</span>
                            {(item.min_hours || 1) > 1 && (
                              <span className="text-[10px] text-neutral-500">min {item.min_hours}h</span>
                            )}
                          </>
                        ) : (
                          <>
                            {!!item.oldPrice && <span className="text-xs text-neutral-400 line-through">₹{item.oldPrice}</span>}
                            <span className="text-lg md:text-xl font-extrabold text-[#EA580C]" data-testid={`svc-price-${item.id}`}>₹{item.price}</span>
                          </>
                        )}
                      </div>
                      {inCart ? (
                        <button onClick={(e) => { e.stopPropagation(); nav('/cart'); }} className="inline-flex items-center gap-1 py-1.5 px-4 rounded-lg bg-[#EA580C] text-white text-xs md:text-sm font-bold">
                          <Check className="w-3.5 h-3.5" /> In Cart ({inCart.qty})
                        </button>
                      ) : (item.pricing_type === 'hourly' || item.basis === 'Hourly') ? (
                        <button onClick={(e) => { e.stopPropagation(); nav(`/services/${categoryId}/${item.id}`); }} className="inline-flex items-center gap-1 py-1.5 px-3 md:px-4 rounded-lg bg-white text-[#EA580C] text-xs md:text-sm font-bold border border-[#EA580C] hover:bg-[#FFF7ED] transition-colors">
                          <Clock className="w-3.5 h-3.5" /> Choose hours
                        </button>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); addToCart(item); }} className="py-1.5 px-5 md:px-6 rounded-lg bg-white text-[#EA580C] text-xs md:text-sm font-bold border border-[#EA580C] hover:bg-[#FFF7ED] transition-colors">
                          Add +
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Right: Cart summary (desktop only) */}
          <div className="hidden md:block">
            <div className="sticky top-24 bg-white border border-neutral-200 rounded-xl p-4">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-[#2E7D32]" />
                <h3 className="font-bold text-sm">Your Cart</h3>
                <span className="ml-auto text-xs text-neutral-500">{cartCount} item{cartCount !== 1 && 's'}</span>
              </div>
              {cart.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-neutral-500">Your cart is empty</p>
                  <p className="text-xs text-neutral-400 mt-1">Add services to book</p>
                </div>
              ) : (
                <>
                  <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
                    {cart.map((c) => (
                      <div key={c.id} className="flex gap-2 text-sm">
                        <img src={c.image} className="w-10 h-10 rounded object-cover flex-shrink-0" alt="" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{c.name}</p>
                          <p className="text-[11px] text-neutral-500">₹{c.price} × {c.qty}</p>
                        </div>
                        <p className="text-xs font-bold">₹{c.price * c.qty}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-dashed border-neutral-200 flex justify-between text-sm">
                    <span className="font-medium">Subtotal</span>
                    <span className="font-extrabold text-[#EA580C]">₹{subtotal}</span>
                  </div>
                  <button onClick={() => nav('/cart')} className="mt-3 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] text-white text-sm font-semibold transition-colors">
                    View Cart →
                  </button>
                </>
              )}
              <div className="mt-4 pt-3 border-t border-neutral-100 flex items-start gap-2 text-[11px] text-neutral-500">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <p>All prices include GST. Cash / UPI accepted after service.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile floating cart CTA */}
      {cart.length > 0 && (
        <div className="md:hidden fixed bottom-16 left-0 right-0 z-40 px-4">
          <button onClick={() => nav('/cart')} className="w-full bg-[#2E7D32] hover:bg-[#276D2A] text-white rounded-xl py-3.5 px-4 flex items-center justify-between shadow-lg transition-colors">
            <span className="font-semibold text-sm">{cartCount} item{cartCount > 1 ? 's' : ''} • ₹{subtotal}</span>
            <span className="font-bold text-sm">View Cart →</span>
          </button>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default ServiceCategory;
